import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Stack,
  Skeleton,
  Card,
  CardContent,
  CardHeader,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles"; // Imported useTheme
import PhotoCarousel from "../components/media/photocarousel";
import ThemeContext from "../theme/ThemeContext";
import { MdMap, MdVisibility, MdCameraAlt } from "react-icons/md";
import { getRandomUpdatesForHomePage } from "../utils/random-updates.js";
import HorizontalBlade, {
  FeatureIcon as OriginalFeatureIcon,
} from "../components/blades/horizontalblade.js";
import VerticalBlade from "../components/blades/verticalblade.js";
import Blade from "../components/blades/mainblade.js";

// Background Images (no changes)
const subtleBackgroundImage1 = "/background-images/reef1.jpg";
const subtleBackgroundImage2 = "/background-images/reef2.jpg";
const subtleBackgroundImage3 = "/background-images/diver1.jpg";
// const subtleBackgroundImage4 = "/background-images/reef3.jpg"; // Keep if used
const subtleBackgroundImage5 = "/background-images/reef4.jpg";

const PageContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
  maxWidth: "xl",
}));

const BladeOverlay =
  "linear-gradient(0deg, rgba(0, 0, 0, 0.55), rgba(0,0,0,0.55))"; // Slightly increased overlay for robust contrast

const BladeTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  fontWeight: 700,
  textAlign: "center",
  textShadow: "1px 1px 4px rgba(0, 0, 0, 0.8)", // Slightly stronger shadow
  marginBottom: theme.spacing(1.5),
  fontSize: theme.typography.pxToRem(32),
  [theme.breakpoints.down("md")]: {
    fontSize: theme.typography.pxToRem(28),
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: theme.typography.pxToRem(24),
  },
}));

const BladeSubheading = styled(Typography)(({ theme }) => ({
  color: "#f0f0f0",
  fontWeight: 600,
  textAlign: "center",
  textShadow: "1px 1px 3px rgba(0, 0, 0, 0.7)",
  lineHeight: 1.6,
  marginBottom: theme.spacing(1),
  fontSize: theme.typography.pxToRem(20),
  [theme.breakpoints.down("md")]: {
    fontSize: theme.typography.pxToRem(18),
  },
}));

const BladeText = styled(Typography)(({ theme }) => ({
  color: "#e0e0e0",
  textAlign: "center",
  lineHeight: 1.7,
  textShadow: "1px 1px 3px rgba(0, 0, 0, 0.7)",
  maxWidth: "650px", // Adjusted maxWidth slightly
  margin: "0 auto",
  marginBottom: theme.spacing(3), // Increased bottom margin before buttons
  fontSize: theme.typography.pxToRem(16),
  [theme.breakpoints.down("md")]: {
    fontSize: theme.typography.pxToRem(15),
  },
}));

const StyledIcon = styled(OriginalFeatureIcon)(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: "3.5rem",
  textShadow: "1px 1px 3px rgba(0, 0, 0, 0.6)",
  marginBottom: theme.spacing(1), // Consistent margin
}));

const ProminentButton = styled(Button)(({ theme }) => ({
  padding: "12px 32px", // Adjusted padding
  fontSize: theme.typography.pxToRem(17),
  fontWeight: 600,
  boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.3)", // Enhanced shadow
  transition: "all 0.25s ease-in-out",
  "&:hover": {
    transform: "scale(1.05) translateY(-2px)", // Added subtle Y transform
    boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.4)",
  },
}));

const UpdateCard = styled(Card)(({ theme }) => ({
  transition: "all 0.3s ease",
  backgroundColor: theme.palette.background.paper,
  display: "flex", // Added for better internal control if needed
  flexDirection: "column", // Added
  height: "100%", // Ensure cards in a row are same height
  "&:hover": {
    transform: "translateY(-5px) scale(1.02)",
    boxShadow: theme.shadows[10],
  },
}));

const NotLoggedInHome = () => {
  const [recentUpdates, setRecentUpdates] = useState(null);
  const [loadingUpdates, setLoadingUpdates] = useState(true);
  const [errorUpdates, setErrorUpdates] = useState(null);
  const { isMobile } = useContext(ThemeContext);
  const theme = useTheme(); // Access theme for inline styles if needed, sx prop preferred

  const bladeFlexCenterSx = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center", // Vertically center the Stack
    alignItems: "center", // Horizontally center the Stack
    textAlign: "center", // General text alignment for the blade
    paddingX: { xs: 2, sm: 3, md: 4 }, // Responsive symmetrical horizontal padding
    paddingY: { xs: 4, sm: 5, md: 6 }, // Responsive vertical padding
    width: "100%", // Ensure blade takes full width of its container
  };

  useEffect(() => {
    const fetchUpdates = async () => {
      setLoadingUpdates(true);
      setErrorUpdates(null);
      try {
        const updatesData = await getRandomUpdatesForHomePage(5, 30);
        if (updatesData === null) {
          setErrorUpdates("Failed to load dive updates.");
          setRecentUpdates(null);
        } else {
          setRecentUpdates(updatesData);
        }
      } catch (error) {
        setErrorUpdates("An unexpected error occurred while fetching updates.");
        setRecentUpdates(null);
      }
      setLoadingUpdates(false);
    };
    fetchUpdates();
  }, []);

  return (
    <PageContainer>

      <HorizontalBlade
        sx={bladeFlexCenterSx}
        style={{
          // Background images still go in 'style' if they are complex strings
          background: `${BladeOverlay}, url(${subtleBackgroundImage1})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Stack direction="column" spacing={2.5} alignItems="center">
          <StyledIcon>
            <MdMap />
          </StyledIcon>
          <BladeTitle>
            Welcome!!! Glad you found us!!!
          </BladeTitle>
       <BladeText>
  We are <strong>mydivingsites.com</strong>, a community of divers sharing dive site locations, and diving information 
  from divers to divers around the world. We are just getting started and would love to have you join us 
  for initial testing and feedback. Reach out to us at{" "}
  <a 
    href="mailto:contactus@mydivingsites.com"
    style={{ color: "white", textDecoration: "underline" }}
  >
    contactus@mydivingsites.com
  </a>
  {" "}to learn more.
</BladeText>
          
        </Stack>
      </HorizontalBlade>


      <HorizontalBlade
        sx={bladeFlexCenterSx}
        style={{
          // Background images still go in 'style' if they are complex strings
          background: `${BladeOverlay}, url(${subtleBackgroundImage5})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Stack direction="column" spacing={2.5} alignItems="center">
          <StyledIcon>
            <MdMap />
          </StyledIcon>
          <BladeTitle>
            Unlock the Collective Wisdom of the Diving Community
          </BladeTitle>
          <BladeText>
            Join a community where divers share their experiences, revealing
            potential dive sites and up-to-date conditions. The more we share,
            the more we all discover.
          </BladeText>
          <ProminentButton
            variant="contained"
            color="secondary"
            size="large"
            href="/register"
          >
            Register and Join!
          </ProminentButton>
        </Stack>
      </HorizontalBlade>

      <HorizontalBlade
        sx={bladeFlexCenterSx}
        style={{
          background: `${BladeOverlay}, url(${subtleBackgroundImage1})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Stack direction="column" spacing={2} alignItems="center">
          <StyledIcon>
            <MdMap />
          </StyledIcon>
          <BladeSubheading>
            Explore Dive Spots Shared by the Community
          </BladeSubheading>
          <BladeText>
            Discover a growing collection of dive locations, enriched by the
            experiences and insights of fellow divers. Access to create and view
            detailed site information awaits.
          </BladeText>
        </Stack>
      </HorizontalBlade>

      <Box sx={{ display: { md: "flex" }, gap: { xs: 0, md: 4 } }}>
        <VerticalBlade
          sx={{ ...bladeFlexCenterSx, flex: 1, paddingY: { xs: 3, sm: 4 } }} // Adjust padding for vertical blades if needed
          style={{
            backgroundImage: `${BladeOverlay}, url(${subtleBackgroundImage2})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Stack direction="column" spacing={2} alignItems="center">
            <StyledIcon>
              <MdVisibility />
            </StyledIcon>
            <BladeSubheading>
              Real-Time Insights from Fellow Divers
            </BladeSubheading>
            <BladeText>
              Access the latest visibility, conditions, and even marine life
              sightings reported directly by divers at the sites you're
              interested in.
            </BladeText>
          </Stack>
        </VerticalBlade>

        <VerticalBlade
          sx={{ ...bladeFlexCenterSx, flex: 1, paddingY: { xs: 3, sm: 4 } }}
          style={{
            backgroundImage: `${BladeOverlay}, url(${subtleBackgroundImage3})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Stack direction="column" spacing={2} alignItems="center">
            <StyledIcon>
              <MdCameraAlt />
            </StyledIcon>
            <BladeSubheading>
              Share Your Dives, Grow Our Shared Knowledge
            </BladeSubheading>
            <BladeText>
              Every dive log, every photo, every tagged species contributes to a
              richer understanding of our underwater world for everyone in the
              community.
            </BladeText>
          </Stack>
        </VerticalBlade>
      </Box>

      <Box sx={{ my: 5, textAlign: "center" }}>
        {" "}
        {/* Increased margin a bit */}
        <Typography variant="h4" component="h2" gutterBottom fontWeight={700}>
          {" "}
          {/* Increased weight */}
          Latest Community Updates
        </Typography>
      </Box>

      <VerticalBlade // This VerticalBlade now also uses bladeFlexCenterSx for consistency
        sx={{ ...bladeFlexCenterSx, paddingY: { xs: 3, sm: 4 } }} // Specific padding for this section
        style={{
          background: `${BladeOverlay}, url(${subtleBackgroundImage5})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {loadingUpdates ? (
          <Stack spacing={2} sx={{ p: 2, width: "100%", maxWidth: "md" }}>
            <Skeleton variant="text" height={40} width="80%" />
            <Skeleton variant="rectangular" width="100%" height={120} />
            <Skeleton variant="text" height={30} width="60%" />
          </Stack>
        ) : errorUpdates ? (
          <Typography
            color="error"
            variant="body1"
            sx={{ fontSize: "1rem", textAlign: "center", p: 2 }}
          >
            {errorUpdates}
          </Typography>
        ) : recentUpdates && recentUpdates.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                lg: "1fr 1fr 1fr",
              },
              gap: 2.5, // Increased gap slightly
              width: "100%", // Ensure grid takes full width available from parent
              maxWidth: theme.breakpoints.values.lg, // Max width for the grid itself
            }}
          >
            {recentUpdates.map((update) => (
              <UpdateCard key={update.diveId} elevation={3}>
                <CardHeader
                  title={
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontSize: "1.05rem", fontWeight: 600 }}
                    >
                      {update.divesiteName}
                    </Typography>
                  }

                  subheader={
                     <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontSize: "0.8rem" }}
                    >
                    {update.divesiteLocation && update.divesiteLocation !== 'Unknown Location' 
                    ? `${update.divesiteLocation} • ` 
                    : ''}
                    {`Updated: ${update.updatedAt}`}
                    </Typography>
                }
                sx={{ pb: 0 }}
                      />
                
                <CardContent
                  sx={{
                    pt: 0.5,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {" "}
                  {/* flexGrow for equal height cards */}
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.9rem", mb: 0.75 }}
                  >
                    Diver:{" "}
                    <span
                      style={{
                        fontWeight: 600,
                        fontStyle: "italic",
                        color: (theme) => theme.palette.primary.main,
                      }}
                    >
                      Register to see
                    </span>
                  </Typography>
                  {update.seenCreatures && update.seenCreatures.length > 0 ? (
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.9rem", mt: 0.75, fontStyle: "italic" }}
                    >
                      Seen: {update.seenCreatures.join(", ")}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.9rem",
                        mt: 0.75,
                        fontStyle: "italic",
                        color: "text.secondary",
                      }}
                    >
                      Seen: Nothing reported.
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.9rem", mt: 0.75, mb: 0.75 }}
                  >
                    Visibility:{" "}
                    <span style={{ fontWeight: 600 }}>
                      {update.visibilityRating}/5
                    </span>
                  </Typography>
                  <Typography
                    component="div"
                    variant="body2"
                    sx={{ fontSize: "0.9rem", flexGrow: 1 }}
                  >
                    {" "}
                    {/* Allow comments to take space */}
                    Comments:{" "}
                    <Typography component="span" sx={{ fontWeight: 400 }}>
                      {update.comments.substring(0, 70)}...
                    </Typography>
                  </Typography>
                </CardContent>
              </UpdateCard>
            ))}
          </Box>
        ) : (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: "1rem", textAlign: "center", p: 2 }}
          >
            No recent dive updates.
          </Typography>
        )}
      </VerticalBlade>

      <Blade
        elevation={2}
        sx={{
          ...bladeFlexCenterSx, // Apply centering and padding
          paddingY: { xs: 4, md: 6 }, // Adjust vertical padding
          background: (theme) => theme.palette.grey[900],
        }}
      >
        {/* Stack is not strictly necessary here if Blade is already flex column and items are self-aligning center */}
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          fontWeight={700}
          color="common.white"
        >
          See the Value in Community Sharing
        </Typography>
        <BladeText
          color="grey.300"
          sx={{ mb: 3, textShadow: "none", maxWidth: "700px" }}
        >
          Browse the incredible underwater moments and locations that our
          community members have documented and shared.
        </BladeText>
        <Box
          sx={{
            width: { xs: "100%", sm: "90%", md: "80%" },
            maxWidth: "800px",
            marginX: "auto",
          }}
        >
          {" "}
          {/* Ensure this box is centered if not full width */}
          <PhotoCarousel
            size={isMobile ? "small" : "medium"}
            numImages={10}
            autoPlay={true}
            interval={5000}
            showArrows={false}
            style={{ borderRadius: "8px", overflow: "hidden", width: "100%" }}
          />
        </Box>
      </Blade>

      <HorizontalBlade
        sx={bladeFlexCenterSx}
        style={{
          backgroundImage: `${BladeOverlay}, url(${subtleBackgroundImage5})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Stack direction="column" spacing={2.5} alignItems="center">
          <StyledIcon>
            <MdMap />
          </StyledIcon>
          <BladeTitle>Ready to Dive In?</BladeTitle>
          <BladeText>
            Join MyDiveSites to share your discoveries, learn from the
            collective experience of other divers, and become part of our
            growing community knowledge base.
          </BladeText>
          <ProminentButton
            variant="contained"
            color="secondary"
            size="large"
            href="/register"
          >
            Register & Start Connecting
          </ProminentButton>
        </Stack>
      </HorizontalBlade>
    </PageContainer>
  );
};

export default NotLoggedInHome;
